import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

const ProtectedRoute = ({ children, allowedRoles = [], requireSuperAdmin = false }) => {
  const { user, userProfile, loading, profileLoading, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading || (user && profileLoading && (allowedRoles?.length > 0 || requireSuperAdmin))) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin routes (/sa/*)
  if (location?.pathname?.startsWith('/sa') && !isSuperAdmin?.()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <Icon name="ShieldAlert" size={32} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Super Admin Only</h2>
          <p className="text-muted-foreground mb-6">This area requires Super Admin privileges.</p>
          <button onClick={() => window.history?.back()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Admin routes (/admin/*)
  if (location?.pathname?.startsWith('/admin') && !['admin', 'super_admin']?.includes(userProfile?.role) && !isSuperAdmin?.()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <Icon name="ShieldAlert" size={32} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">This area requires Admin privileges.</p>
          <button onClick={() => window.history?.back()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check role-based access if allowedRoles specified
  if (allowedRoles?.length > 0 && userProfile) {
    const userRole = userProfile?.role;
    const userGlobalRole = userProfile?.global_role;
    const hasAccess = allowedRoles?.includes(userRole) ||
      (userGlobalRole === 'super_admin') ||
      allowedRoles?.includes('super_admin') && isSuperAdmin?.();

    if (!hasAccess) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
              <Icon name="ShieldAlert" size={32} className="text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-6">You do not have permission to access this page.</p>
            <button onClick={() => window.history?.back()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;